import React, { useEffect, useState, useRef } from "react";
import "./ClientsPage.css";

const API_BASE = process.env.REACT_APP_API_BASE || `${process.env.REACT_APP_API_URL}`;

function apiHeaders(json = true) {
  const headers = {};
  if (json) headers["Content-Type"] = "application/json";
  const token = localStorage.getItem("token");
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const timeoutRef = useRef(null);

  function showMsg(text) {
    setError(text);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setError(""), 4000);
  }

  async function loadClients() {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/clients`, {
        headers: apiHeaders(false),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `Failed to load clients (${res.status})`);
      }
      const data = await res.json();
      setClients(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("loadClients error:", err);
      setClients([]);
      showMsg(err.message || "Failed to load clients");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadClients();
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // filtered view
  const filtered = clients.filter((c) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      (c.name || "").toString().toLowerCase().includes(q) ||
      (c.email || "").toString().toLowerCase().includes(q) ||
      (c.phone || "").toString().toLowerCase().includes(q) ||
      (c.contact_person || "").toString().toLowerCase().includes(q)
    );
  });

  // helper to show truncated address
  function shortAddress(addr, max = 60) {
    if (!addr) return "—";
    const s = addr.toString();
    return s.length > max ? s.slice(0, max - 1) + "…" : s;
  }

  return (
    <div className="clients-page">
      <header className="clients-header">
        <div>
          <h2>Clients</h2>
          <p className="sub">List of clients and their contact information (view only)</p>
        </div>

        <div className="clients-controls">
          <input
            className="search"
            placeholder="Search by name, email, phone or contact person…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button className="btn" onClick={loadClients} disabled={loading}>
            Refresh
          </button>
        </div>
      </header>

      {error && <div className="clients-error">{error}</div>}

      <section className="clients-list">
        <div className="table-wrapper">
          <table className="clients-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Contact Person</th>
                <th>Address</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="empty">Loading…</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty">No clients found</td>
                </tr>
              ) : (
                filtered.map((c) => (
                  <tr key={c.id}>
                    <td>{c.id}</td>
                    <td>{c.name}</td>
                    <td>{c.email || "—"}</td>
                    <td>{c.phone || "—"}</td>
                    <td>{c.contact_person || "—"}</td>
                    <td title={c.address || ""}>{shortAddress(c.address)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
